import * as Sentry from '@sentry/react';
import { ComponentType, useCallback } from 'react';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';

/**
 * Higher-order component using Sentry's ErrorBoundary for automatic error tracking.
 * This version allows the use of custom hooks inside the wrapped component.
 */
export const withSentryErrorBoundary = <P extends object>(
  WrappedComponent: ComponentType<P>,
  options?: {
    componentName?: string;
    fallback?: (props: {
      error: unknown;
      componentStack: string;
      eventId: string;
      resetError(): void;
    }) => JSX.Element;
    onError?: (
      error: unknown,
      componentStack: string | undefined,
      eventId: string
    ) => void;
  }
) => {
  const componentName =
    options?.componentName ||
    WrappedComponent.displayName ||
    WrappedComponent.name ||
    'Component';

  const FallbackComponent =
    options?.fallback ||
    function FallbackWithSpinner() {
      return <PosSpinner />;
    };

  // Return a functional component that can use hooks
  function ComponentWithErrorBoundary(props: P) {
    const { showErrorDialog } = useClientLogContext();
    const onError = useCallback(
      (error: unknown, componentStack: string | undefined, eventId: string) => {
        if (options?.onError) {
          return options.onError(error, componentStack, eventId);
        }
        showErrorDialog(eventId, error as ErrorTypes, {
          customerHeader: `Unhandled error in ${componentName}`,
        });
      },
      [showErrorDialog]
    );
    return (
      <Sentry.ErrorBoundary
        fallback={(fallbackProps) => <FallbackComponent {...fallbackProps} />}
        showDialog={false}
        beforeCapture={(scope) => {
          scope.setTag('component', componentName);
          scope.setContext('errorBoundary', {
            componentName,
          });
        }}
        onError={onError}
      >
        <WrappedComponent {...props} />
      </Sentry.ErrorBoundary>
    );
  }

  ComponentWithErrorBoundary.displayName = `withSentryErrorBoundary(${componentName})`;

  return ComponentWithErrorBoundary;
};
